import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { format, subDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function App() {
  const [summary, setSummary] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [dailyUptime, setDailyUptime] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, incidentsRes] = await Promise.all([
        fetch(`${API_URL}/api/summary`),
        fetch(`${API_URL}/api/incidents`),
      ]);

      if (!summaryRes.ok || !incidentsRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const summaryData = await summaryRes.json();
      const incidentsData = await incidentsRes.json();

      setSummary(summaryData);
      setIncidents(incidentsData.incidents || []);

      // Fetch daily uptime for each service (120 days)
      const uptimePromises = summaryData.services.map(async (service) => {
        try {
          const res = await fetch(`${API_URL}/api/services/${service.slug}/daily-uptime?days=120`);
          if (res.ok) {
            const data = await res.json();
            return { slug: service.slug, data: data.daily_uptime };
          }
        } catch (e) {
          console.error(`Failed to fetch uptime for ${service.slug}`);
        }
        return { slug: service.slug, data: [] };
      });

      const uptimeResults = await Promise.all(uptimePromises);
      const uptimeMap = {};
      uptimeResults.forEach(({ slug, data }) => {
        uptimeMap[slug] = data;
      });
      setDailyUptime(uptimeMap);

      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getStatusLabel = (status) => {
    switch (status) {
      case 'operational':
        return 'All Systems Operational';
      case 'partial_outage':
        return 'Partial System Outage';
      case 'major_outage':
        return 'Major System Outage';
      default:
        return 'Checking Status...';
    }
  };

  const calculateOverallUptime = (slug) => {
    const data = dailyUptime[slug] || [];
    if (data.length === 0) return 100;
    const totalChecks = data.reduce((sum, d) => sum + d.total_checks, 0);
    const upChecks = data.reduce((sum, d) => sum + d.up_checks, 0);
    if (totalChecks === 0) return 100;
    return ((upChecks / totalChecks) * 100).toFixed(2);
  };

  const handleDayHover = (e, day, serviceName) => {
    const rect = e.target.getBoundingClientRect();
    setTooltip({
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      content: `${serviceName}: ${day.uptime_percentage}% on ${format(new Date(day.date), 'MMM d, yyyy')}`
    });
  };

  const handleDayLeave = () => {
    setTooltip(null);
  };

  const formatIncidentTime = (isoString) => {
    try {
      return format(new Date(isoString), 'MMM d, h:mm a');
    } catch {
      return 'Unknown';
    }
  };

  const groupIncidentsByDate = () => {
    const grouped = {};
    const today = new Date();
    
    // Create entries for last 7 days
    for (let i = 0; i < 7; i++) {
      const date = subDays(today, i);
      const dateKey = format(date, 'yyyy-MM-dd');
      grouped[dateKey] = {
        date: format(date, 'MMMM d, yyyy'),
        incidents: []
      };
    }
    
    // Add incidents to their dates
    incidents.forEach(incident => {
      const dateKey = format(new Date(incident.started_at), 'yyyy-MM-dd');
      if (grouped[dateKey]) {
        grouped[dateKey].incidents.push(incident);
      }
    });
    
    return Object.values(grouped);
  };

  if (loading) {
    return (
      <div className="app">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <div className="loading">
          <XCircle size={48} color="#ef4444" />
          <p>Failed to load status: {error}</p>
          <button onClick={fetchData} style={{ 
            padding: '0.5rem 1rem', 
            background: '#f1f5f9', 
            border: '1px solid #e2e8f0', 
            borderRadius: '8px',
            cursor: 'pointer'
          }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const incidentGroups = groupIncidentsByDate();

  return (
    <div className="app">
      {tooltip && (
        <div className="tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          {tooltip.content}
        </div>
      )}
      
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <img src="/logo.png" alt="Intugle" className="logo-img" />
            <h1>Intugle</h1>
          </div>
        </div>
      </header>

      <main className="main">
        <section className="status-banner-container">
          <div 
            className={`status-banner ${summary?.overall_status || 'operational'}`}
            data-testid="status-banner"
          >
            {summary?.overall_status === 'operational' ? (
              <CheckCircle className="status-icon" />
            ) : (
              <XCircle className="status-icon" />
            )}
            {getStatusLabel(summary?.overall_status)}
          </div>
        </section>

        <section className="services-section">
          <div className="section-header">
            <p className="section-subtext">
              Uptime over the past 120 days. <a href="#history">View historical uptime.</a>
            </p>
          </div>
          
          <div data-testid="services-list">
            {summary?.services?.map((service) => {
              const uptimeData = dailyUptime[service.slug] || [];
              const overallUptime = calculateOverallUptime(service.slug);
              
              return (
                <div 
                  key={service.slug} 
                  className="service-card"
                  data-testid={`service-card-${service.slug}`}
                >
                  <div className="service-header">
                    <div className="service-info">
                      <a 
                        href={service.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="service-name-link"
                      >
                        {service.name}
                      </a>
                    </div>
                    <div className={`service-status-badge ${service.current_status || 'unknown'}`}>
                      <span className={`status-dot ${service.current_status || 'unknown'}`}></span>
                      {service.current_status === 'up' ? 'Operational' : 
                       service.current_status === 'down' ? 'Down' : 'Checking'}
                    </div>
                  </div>
                  
                  <div className="uptime-container">
                    <span className="uptime-label">120 days ago</span>
                    <div className="uptime-bar">
                      {uptimeData.length > 0 ? (
                        uptimeData.map((day, idx) => (
                          <div
                            key={idx}
                            className={`uptime-day ${day.status}`}
                            onMouseEnter={(e) => handleDayHover(e, day, service.name)}
                            onMouseLeave={handleDayLeave}
                            title={`${day.date}: ${day.uptime_percentage}%`}
                          />
                        ))
                      ) : (
                        // Show empty bars if no data yet
                        Array.from({ length: 120 }, (_, idx) => (
                          <div key={idx} className="uptime-day no-data" />
                        ))
                      )}
                    </div>
                    <span className="uptime-percentage">
                      {overallUptime}% uptime
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="legend">
            <div className="legend-item">
              <div className="legend-color operational"></div>
              <span>Operational</span>
            </div>
            <div className="legend-item">
              <div className="legend-color degraded"></div>
              <span>Degraded Performance</span>
            </div>
            <div className="legend-item">
              <div className="legend-color outage"></div>
              <span>Major Outage</span>
            </div>
            <div className="legend-item">
              <div className="legend-color no-data"></div>
              <span>No data</span>
            </div>
          </div>
        </section>

        <section className="incidents-section" id="history">
          <h2 className="section-title">Past Incidents</h2>
          
          {incidentGroups.map((group, idx) => (
            <div key={idx} className="incident-day">
              <div className="incident-date">{group.date}</div>
              {group.incidents.length > 0 ? (
                group.incidents.map((incident, iIdx) => (
                  <div 
                    key={iIdx}
                    className={`incident-card ${incident.resolved_at ? 'resolved' : 'active'}`}
                  >
                    <div className="incident-header">
                      <span className="incident-title">{incident.service_name}</span>
                      <span className={`incident-badge ${incident.resolved_at ? 'resolved' : 'active'}`}>
                        {incident.resolved_at ? 'Resolved' : 'Investigating'}
                      </span>
                    </div>
                    <p className="incident-description">{incident.description}</p>
                    <div className="incident-time">
                      {formatIncidentTime(incident.started_at)}
                      {incident.resolved_at && ` - Resolved ${formatIncidentTime(incident.resolved_at)}`}
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-incidents-day">No incidents reported.</p>
              )}
            </div>
          ))}
        </section>
      </main>

      <footer className="footer">
        <p>
          Intugle Status Page • Real-time system monitoring
        </p>
      </footer>
    </div>
  );
}

export default App;
